'use client';

import { useAuth0 } from '@auth0/auth0-react';
import { usePathname, useRouter } from 'next/navigation';
import querystring from 'query-string';
import { ReactNode, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { AuthClientInterface, useAuth } from '@/hooks/auth';
import { useUser } from '@/hooks/store';
import { routes } from '@/routes';
import { setBannerMessage, setLoading, setUser } from '@/store/actions';
import { User } from '@/types/store/reducers';
import { ErrorWithMessage, isCalmError, isErrorWithMessage } from '@/utils/apiRequest/errors';
import { calmLogger } from '@/utils/calmLogger';
import { ILLEGAL_USER_CONFIGURATION, UNKNOWN_USER_ROLE } from '@/utils/RBAC';

import FullPageLoader from '../ui/FullPageLoader';

interface AuthenticatedPageProps {
	children: ReactNode;
}

/**
 * Client component that wraps page content with authentication logic.
 *
 * This component can be used within server components to provide authentication
 * for specific pages in the Next.js app router.
 *
 * @example
 * ```tsx
 * // In a server component page
 * export default function DashboardPage() {
 *   return (
 *     <AuthenticatedPage>
 *       <div>Protected content here</div>
 *     </AuthenticatedPage>
 *   );
 * }
 * ```
 */
export default function AuthenticatedPage({ children }: AuthenticatedPageProps): ReactNode {
	const [hasInitializedApplicationData, setHasInitializedApplicationData] = useState(false);
	const router = useRouter();
	const pathname = usePathname();
	const { loading: isUserLoading, user } = useUser();
	const { isLoading, isAuthenticated } = useAuth0();
	const [authClient] = useAuth();
	const dispatch = useDispatch();

	function handleInvalidUserErrors(err: ErrorWithMessage): void {
		if (err.message === ILLEGAL_USER_CONFIGURATION) {
			dispatch(
				setBannerMessage({
					message: 'Invalid user configuration. Please contact the system administrator to resolve.',
					isError: true,
					flash: false,
				}),
			);
		} else if (err.message === UNKNOWN_USER_ROLE) {
			dispatch(
				setBannerMessage({
					message:
						'User role not recognized for this application. Please contact the system administrator to resolve.',
					isError: true,
					flash: false,
				}),
			);
		}
	}

	async function getUser(_authClient: AuthClientInterface): Promise<User> {
		// eslint-disable-next-line no-async-promise-executor
		return new Promise(async (resolve, reject) => {
			try {
				const calmUser = await _authClient.getCalmUserFromState();
				if (!calmUser) {
					return resolve({});
				}
				resolve(calmUser);
			} catch (err) {
				try {
					await _authClient.signOut();
				} catch (e) {
					// Do nothing here
					calmLogger.error('Failed to sign out in AuthenticatedPage getUser', {}, e);
				}
				if (isErrorWithMessage(err)) {
					handleInvalidUserErrors(err);
				}
				reject(new Error('failed to load user'));
			}
		});
	}

	async function initializeApplicationData(_authClient: AuthClientInterface): Promise<void> {
		try {
			const userDetails = await getUser(_authClient);
			await dispatch(setUser(userDetails));
			// Wait for next tick to ensure user state is updated before setting loading to false
			await new Promise(resolve => setTimeout(resolve, 0));
			dispatch(setLoading(false));
			setHasInitializedApplicationData(true);
		} catch (err) {
			// TODO: Eventually handle this error gracefully
			calmLogger.error(
				(isErrorWithMessage(err) ? err.message : isCalmError(err) ? err.data.error.message : undefined) ??
					'Error in AuthenticatedPage initializeApplicationData',
				{},
				err,
			);
		}
	}

	/**
	 * Initialize the user state
	 */
	useEffect(() => {
		if (!hasInitializedApplicationData && authClient && !isLoading) {
			initializeApplicationData(authClient).catch(err =>
				calmLogger.error('Error in AuthenticatedPage initializeApplicationData catch', {}, err),
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [hasInitializedApplicationData, authClient, isLoading]);

	/**
	 * Check to see if we should redirect to /login after the initial load
	 */
	useEffect(() => {
		// Only check after we've async grabbed the access token from okta tokenManager
		// Also wait for Auth0 to finish loading to avoid race conditions during OAuth callback
		if (!isUserLoading && hasInitializedApplicationData && !isLoading && pathname) {
			// Check for lack of session token stored
			// Also verify user is not authenticated with Auth0 to avoid redirect loops during token initialization
			if (!user?.accessToken && !isAuthenticated && !pathname.startsWith('/login')) {
				const redirectUrl = encodeURIComponent(pathname);
				const query = querystring.parse(window.location.search);
				query.returnTo = redirectUrl;
				const searchParams = new URLSearchParams(query as Record<string, string>);
				router.push(routes.login(searchParams));
			}
		}
		// NOTE: pathname intentionally omitted from deps to avoid re-running auth check on every navigation.
		// This effect only needs to run when authentication state changes, not on every route change.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		isUserLoading,
		hasInitializedApplicationData,
		isLoading,
		isAuthenticated,
		user?.accessToken,
		router,
		dispatch,
	]);

	// Show loading state while authentication is in progress
	if (isUserLoading || !hasInitializedApplicationData || isLoading) {
		return <FullPageLoader />;
	}

	// Render the children once authentication is complete
	return children;
}
